import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Avatar,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import { categoryAPI } from "../../services/api";
import { createTextValidation } from "../common/TextValidation";

interface SubcategoryFormData {
  id?: string;
  name: string;
  status: string;
  image?: File | string;
}

interface SubcategoryDialogProps {
  open: boolean;
  onClose: () => void;
  subcategory?: SubcategoryFormData | null;
  onSave: (subcategory: SubcategoryFormData) => void;
  mode: "add" | "edit" | "view";
  categoryName?: string;
  parentId?: string;
}

const schema = yup.object().shape({
  name: createTextValidation({
    title: "Subcategory name",
    minLength: 3,
    maxLength: 100,
  }),
  status: yup.string().required("Status is required"),
});

const SubcategoryDialog: React.FC<SubcategoryDialogProps> = ({
  open,
  onClose,
  subcategory,
  onSave,
  mode,
  categoryName,
  parentId,
}) => {
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [mediaChanged, setMediaChanged] = useState(false);
    // Track deleted subcategory image name (not array)
    const [deletedSubcategoryMedia, setDeletedSubcategoryMedia] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SubcategoryFormData>({
    resolver: yupResolver(schema),
    defaultValues: {
      name: "",
      status: "1",
    },
  });

  useEffect(() => {
    if (subcategory) {
      reset({
        name: subcategory.name,
        status: subcategory.status || "1",
      });
      if (subcategory.image && typeof subcategory.image === "string") {
        // Construct the full image URL using the media download API
        const imageUrl = categoryAPI.getMediaDownloadUrl(subcategory.image);
        setPreviewUrl(imageUrl);
        setMediaChanged(false);
  setDeletedSubcategoryMedia(null);
      }
    } else {
      reset({
        name: "",
        status: "1",
      });
      setSelectedFile(null);
      setPreviewUrl("");
      setMediaChanged(false);
  setDeletedSubcategoryMedia(null);
    }
  }, [subcategory, reset]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const supportedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!supportedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF)");
        event.target.value = "";
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("File size must be less than 2MB");
        event.target.value = "";
        return;
      }

      setSelectedFile(file);
      setMediaChanged(true);
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    // Always reset input so same file can be selected again
    event.target.value = "";
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setMediaChanged(true);
    // Track deleted image name in edit mode
    if (mode === "edit" && subcategory?.image && typeof subcategory.image === "string") {
      setDeletedSubcategoryMedia(subcategory.image);
    }
  };

  const onSubmit: SubmitHandler<SubcategoryFormData> = async (data) => {
    setLoading(true);
    try {
      let uploadedImageName = "";

      // Step 1: Upload image if selected and media has changed
      if (selectedFile && mediaChanged) {
        const formData = new FormData();
        formData.append("fileKey", selectedFile, selectedFile.name);

        const uploadResponse = await categoryAPI.uploadCategoryMedia(formData);
        uploadedImageName = uploadResponse.data.result.files.fileKey[0].name;
      }

      // Step 2: Prepare subcategory data
      let subcategoryData: any = {
        categorystatus: data.status,
        name: data.name.trim(),
        parentId: parentId,
      };
      if (mode === "add") {
        if (uploadedImageName) {
          subcategoryData.categorymedia = { name: uploadedImageName };
        }
      } else if (mode === "edit" && subcategory?.id) {
        if (mediaChanged) {
          if (selectedFile && uploadedImageName) {
            subcategoryData.categorymedia = { name: uploadedImageName };
          } else if (!selectedFile) {
            // Image removed
            subcategoryData.categorymedia = {};
          }
        }
        // Pass deleted subcategory media if any
        if (deletedSubcategoryMedia) {
          subcategoryData.deletedcategorymedia = deletedSubcategoryMedia;
        }
      }

      // Step 3: Call add/update subcategory API
      if (mode === "add") {
        await categoryAPI.addCategory(subcategoryData);
        toast.success("Subcategory added successfully!");
      } else if (mode === "edit" && subcategory?.id) {
        await categoryAPI.updateCategory(subcategory.id, subcategoryData);
        toast.success("Subcategory updated successfully!");
      }

      // Reset form and state after successful submission
      reset({
        name: "",
        status: "1",
      });
      setSelectedFile(null);
      setPreviewUrl("");
      setMediaChanged(false);

      onSave(data);
      onClose();
    } catch (error: any) {
      // Handle specific error response structure
      let errorMessage = "Failed to save subcategory.";

      if (error.response?.data?.detail?.responseData?.message) {
        // Extract the specific error message from the API response
        errorMessage = error.response.data.detail.responseData.message;
      } else if (error.response?.data?.detail?.responseMessage) {
        // Fallback to general response message
        errorMessage = error.response.data.detail.responseMessage;
      } else if (error.message) {
        // Fallback to error message
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
  reset();
  setSelectedFile(null);
  setPreviewUrl("");
  setMediaChanged(false);
  setDeletedSubcategoryMedia(null);
  onClose();
  };

  const getDialogTitle = () => {
    switch (mode) {
      case "add":
        return `Add New Subcategory${
          categoryName ? ` for ${categoryName}` : ""
        }`;
      case "edit":
        return `Edit Subcategory${categoryName ? ` in ${categoryName}` : ""}`;
      case "view":
        return `View Subcategory${categoryName ? ` in ${categoryName}` : ""}`;
      default:
        return "Subcategory";
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{getDialogTitle()}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ position: "relative" }}>
          {/* Loader overlay */}
          {loading && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                bgcolor: "rgba(255,255,255,0.7)",
                zIndex: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              opacity: loading ? 0.5 : 1,
              pointerEvents: loading ? "none" : "auto",
            }}
          >
            {/* Subcategory Name */}
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Subcategory Name"
                  fullWidth
                  disabled={mode === "view"}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />

            {/* Status Field (only for edit mode) */}
            {mode === "edit" && (
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.status}>
                    <InputLabel>Status</InputLabel>
                    <Select {...field} label="Status">
                      <MenuItem value="1">Active</MenuItem>
                      <MenuItem value="0">Inactive</MenuItem>
                    </Select>
                    {errors.status && (
                      <FormHelperText>{errors.status.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            )}

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Subcategory Image
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You can upload image files up to 2MB (JPEG, PNG, GIF).
              </Typography>

              {/* Image Preview */}
              {previewUrl && (
                <Box
                  sx={{ mb: 2, position: "relative", display: "inline-block" }}
                >
                  <Avatar
                    src={previewUrl}
                    alt="Subcategory"
                    sx={{ width: 100, height: 100, mb: 1 }}
                  />
                  {mode !== "view" && (
                    <IconButton
                      onClick={removeImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "error.main",
                        color: "white",
                        "&:hover": { bgcolor: "error.dark" },
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* Upload Button */}
              {mode !== "view" && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  sx={{ mt: 1 }}
                >
                  Upload Image
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                  />
                </Button>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {mode !== "view" && (
            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default SubcategoryDialog;
